import {
  Alert,
  AlertTitle,
  Button,
  Card,
  CardTitle,
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  Skeleton,
} from '@design-system';

import {
  PageHeader,
  useCarrierSummaries,
  type CarrierSummary,
} from '@/features/quote-and-apply-builder';

import { Building2, FileText, InfoIcon, MapPin, Plus } from 'lucide-react';

interface CarrierListProps {
  onNewForm: () => void;
  onSelectCarrier: (carrier: CarrierSummary) => void;
}

export function CarrierList({ onNewForm, onSelectCarrier }: CarrierListProps) {
  const { data: carriers, isLoading, error } = useCarrierSummaries();

  const isEmptyState = !isLoading && !error && !carriers?.length;
  const isDataLoaded = !isLoading && !error && carriers && carriers.length > 0;

  return (
    <div className="mx-auto max-w-5xl px-6 pb-12 pt-6">
      <PageHeader
        title="Quote & Apply Form Builder"
        description="Manage carrier form definitions across states"
      >
        <Button onClick={onNewForm}>
          <Plus />
          Create New Form
        </Button>
      </PageHeader>

      {isLoading && (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 6 }).map((_, index) => (
            <Card
              key={index}
              className="block p-4"
            >
              <div className="flex items-center gap-4">
                <Skeleton className="size-12 rounded-md" />
                <div className="space-y-2">
                  <Skeleton className="h-5 w-32" />
                  <Skeleton className="h-4 w-48" />
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {error && (
        <Alert variant="destructive">
          <InfoIcon />
          <AlertTitle>Failed to load carriers. Please try again.</AlertTitle>
        </Alert>
      )}

      {isEmptyState && (
        <Empty>
          <EmptyHeader>
            <EmptyMedia variant="icon">
              <Building2 />
            </EmptyMedia>
            <EmptyDescription>No carriers with form definitions found.</EmptyDescription>
          </EmptyHeader>
          <EmptyContent>
            <Button
              size="sm"
              onClick={onNewForm}
            >
              <Plus />
              Create Your First Form
            </Button>
          </EmptyContent>
        </Empty>
      )}

      {isDataLoaded && (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {carriers.map(carrier => (
            <Card
              key={carrier.insuranceCarrierId}
              onClick={() => onSelectCarrier(carrier)}
              className="hover:border-primary group flex cursor-pointer flex-row items-center gap-4 p-4 transition-all hover:shadow-none"
            >
              <div className="bg-primary/10 group-hover:bg-primary text-primary group-hover:text-primary-foreground flex size-14 items-center justify-center rounded-md transition-colors">
                <Building2 className="size-6" />
              </div>
              <div className="flex flex-col gap-2">
                <CardTitle>{carrier.carrierName}</CardTitle>
                <div className="text-foreground flex items-center gap-4 text-sm">
                  <span className="flex items-center gap-1">
                    <FileText className="size-3.5" />
                    {carrier.totalForms} {carrier.totalForms === 1 ? 'form' : 'forms'}
                  </span>
                  <span className="flex items-center gap-1">
                    <MapPin className="size-3.5" />
                    {carrier.totalStates} {carrier.totalStates === 1 ? 'state' : 'states'}
                  </span>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
